/**
 * Sidebar.jsx — Updated
 *
 * Changes vs original:
 *   - SALES group now points to /invoices (new module) instead of accounting/invoices
 *   - QUOTATIONS group now points to /quotations, /sales-orders etc (new module)
 *   - Added GST Reports link
 *   - Added Invoice Dashboard link
 *   - Original accounting routes kept as fallback
 *
 * PASTE AT: frontend/src/components/layout/Sidebar.jsx  (REPLACE entire file)
 */

import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  HomeIcon, UsersIcon, FolderIcon, CheckCircleIcon, CalendarIcon,
  CubeIcon, DocumentTextIcon, ArrowRightOnRectangleIcon,
  ChevronDownIcon, Bars3Icon, XMarkIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon,
  ArrowUpCircleIcon, ShoppingCartIcon, DocumentDuplicateIcon,
  ArrowDownCircleIcon, BanknotesIcon, QueueListIcon,
  UserGroupIcon, TruckIcon, WrenchScrewdriverIcon, BuildingOffice2Icon, Cog6ToothIcon,
  PresentationChartLineIcon, DocumentChartBarIcon, ScaleIcon,
  ClipboardDocumentListIcon, ArrowUturnLeftIcon,
  FolderOpenIcon, BookOpenIcon,
} from '@heroicons/react/24/outline';

// ─── Contacts ─────────────────────────────────────────────────
const CON = [
  { slug: 'contacts/customers',   label: 'Customers',   Icon: UserGroupIcon },
  { slug: 'contacts/vendors',     label: 'Vendors',     Icon: TruckIcon },
  { slug: 'contacts/contractors', label: 'Contractors', Icon: WrenchScrewdriverIcon },
];

// ─── Sales dropdown — NEW INVOICE MODULE ─────────────────────
const SALES = [
  { slug: 'invoices',    label: 'Tax Invoices',  Icon: DocumentTextIcon },
  { slug: 'credit-notes',label: 'Credit Notes',  Icon: ArrowUturnLeftIcon },
];

// ─── Purchases dropdown ───────────────────────────────────────
const PURCHASES = [
  { slug: 'accounting/purchases',      label: 'Purchases',       Icon: ShoppingCartIcon },
  { slug: 'accounting/purchaseorders', label: 'Purchase Orders', Icon: ClipboardDocumentListIcon },
  { slug: 'accounting/debitnotes',     label: 'Debit Notes',     Icon: ArrowUturnLeftIcon },
];

// ─── Quotations+ dropdown — NEW INVOICE MODULE ───────────────
const QUOTATIONS = [
  { slug: 'quotations',        label: 'Quotations',          Icon: DocumentDuplicateIcon },
  { slug: 'proforma',          label: 'Pro Forma Invoices',  Icon: ClipboardDocumentListIcon },
  { slug: 'sales-orders',      label: 'Sales Orders',        Icon: QueueListIcon },
  { slug: 'delivery-challans', label: 'Delivery Challans',   Icon: TruckIcon },
];

// ─── Expenses+ dropdown ───────────────────────────────────────
const EXPENSES = [
  { slug: 'accounting/expenses', label: 'Expenses',        Icon: ArrowDownCircleIcon },
  { slug: 'accounting/income',   label: 'Indirect Income', Icon: BanknotesIcon },
];

// ─── Reports dropdown ─────────────────────────────────────────
const REPORTS = [
  { slug: 'reports/sales',        label: 'Sales Report',    Icon: PresentationChartLineIcon },
  { slug: 'reports/gst',          label: 'GST Report',      Icon: DocumentChartBarIcon },
  { slug: 'reports/outstanding',  label: 'Outstanding',     Icon: BanknotesIcon },
  { slug: 'reports/collection',   label: 'Collection',      Icon: BanknotesIcon },
  { slug: 'accounting/gst',       label: 'GST (Legacy)',    Icon: DocumentChartBarIcon },
  { slug: 'accounting/ledger',    label: 'Party Ledger',    Icon: BookOpenIcon },
  { slug: 'reports/pl',           label: 'Profit & Loss',   Icon: ScaleIcon },
];

// ─── Admin nav ────────────────────────────────────────────────
const ADMIN = [
  { slug: '',          label: 'Dashboard',  Icon: HomeIcon },
  { slug: 'projects',  label: 'Projects',   Icon: FolderIcon },

  { group: true, label: 'Sales',       Icon: ArrowUpCircleIcon,            items: SALES },
  { group: true, label: 'Quotations+', Icon: DocumentDuplicateIcon,        items: QUOTATIONS },
  { group: true, label: 'Purchases',   Icon: ShoppingCartIcon,             items: PURCHASES },
  { group: true, label: 'Expenses+',   Icon: ArrowDownCircleIcon,          items: EXPENSES },
  { group: true, label: 'Contacts',    Icon: BuildingOffice2Icon,          items: CON },
  { slug: 'tasks',     label: 'Tasks',      Icon: CheckCircleIcon },
  { slug: 'calendar',  label: 'Calendar',   Icon: CalendarIcon },

 
  { group: true, label: 'Reports',     Icon: PresentationChartLineIcon,    items: REPORTS },

  { slug: 'inventory', label: 'Inventory',  Icon: CubeIcon },
  { slug: 'documents', label: 'Documents',  Icon: FolderOpenIcon },
  { slug: 'dataentry', label: 'Data Entry', Icon: DocumentTextIcon },
  { slug: 'settings',  label: 'Settings',   Icon: Cog6ToothIcon },
  { slug: 'users',     label: 'Users',      Icon: UsersIcon },
];

// ─── Employee nav ─────────────────────────────────────────────
const EMP = [
  { slug: '',         label: 'Dashboard', Icon: HomeIcon },
  { slug: 'projects', label: 'Projects',  Icon: FolderIcon },
  { group: true, label: 'Sales',       Icon: ArrowUpCircleIcon,     items: SALES },
  { group: true, label: 'Quotations+', Icon: DocumentDuplicateIcon, items: QUOTATIONS },
  { group: true, label: 'Purchases',   Icon: ShoppingCartIcon,      items: PURCHASES },
  { group: true, label: 'Expenses+',   Icon: ArrowDownCircleIcon,   items: EXPENSES },
  { slug: 'tasks',    label: 'My Tasks',  Icon: CheckCircleIcon },
  { slug: 'calendar', label: 'Calendar',  Icon: CalendarIcon },
  { slug: 'dataentry',label: 'Data Entry',Icon: DocumentTextIcon },
  { slug: 'settings', label: 'Settings',  Icon: Cog6ToothIcon },
];

// ─── Accountant nav ───────────────────────────────────────────
const ACCT = [
  { slug: '', label: 'Dashboard', Icon: HomeIcon },
  { group: true, label: 'Contacts',    Icon: BuildingOffice2Icon,       items: CON },
  { group: true, label: 'Sales',       Icon: ArrowUpCircleIcon,         items: SALES },
  { group: true, label: 'Quotations+', Icon: DocumentDuplicateIcon,     items: QUOTATIONS },
  { group: true, label: 'Purchases',   Icon: ShoppingCartIcon,          items: PURCHASES },
  { group: true, label: 'Expenses+',   Icon: ArrowDownCircleIcon,       items: EXPENSES },
  { group: true, label: 'Reports',     Icon: PresentationChartLineIcon, items: REPORTS },
  { slug: 'settings', label: 'Settings', Icon: Cog6ToothIcon },
];

export const CONTRACTOR_NAV = [
  { slug: '',         label: 'My Invoices', Icon: DocumentTextIcon },
  { slug: 'settings', label: 'My Settings', Icon: Cog6ToothIcon },
];

export const REPORTS_NAV = [
  { slug: 'analytics',           label: 'Analytics',      Icon: PresentationChartLineIcon },
  { slug: 'reports/pl',          label: 'Profit & Loss',  Icon: ScaleIcon },
  { slug: 'reports/gst',         label: 'GST Report',     Icon: DocumentChartBarIcon },
  { slug: 'accounting/ledger',   label: 'Ledger',         Icon: UserGroupIcon },
];

// ─── Group component ──────────────────────────────────────────
function Group({ item, base, collapsed }) {
  const loc = useLocation();
  const active = item.items.some(
    c => loc.pathname === base + c.slug || loc.pathname.startsWith(base + c.slug + '/')
  );
  const [open, setOpen] = useState(active);

  if (collapsed) return (
    <div className={'flex justify-center py-2 ' + (active ? 'text-indigo-600' : 'text-gray-400')}>
      <item.Icon className="w-5 h-5" />
    </div>
  );

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className={'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ' +
          (active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-100')}
      >
        <item.Icon className="w-5 h-5 flex-shrink-0" />
        <span className="text-sm font-medium flex-1 text-left">{item.label}</span>
        <ChevronDownIcon className={'w-3.5 h-3.5 transition-transform duration-200 ' + (open ? 'rotate-180' : '')} />
      </button>

      {open && (
        <div className="ml-4 mt-0.5 pl-3 border-l-2 border-indigo-100 space-y-0.5">
          {item.items.map(c => (
            <NavLink
              key={c.slug}
              to={base + c.slug}
              end
              className={({ isActive }) =>
                'flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-sm transition-colors ' +
                (isActive
                  ? 'bg-indigo-600 text-white font-medium'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800')
              }
            >
              <c.Icon className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{c.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Item component ───────────────────────────────────────────
function Item({ slug, label, Icon, base, collapsed, onClose }) {
  const to     = base + slug;
  const isRoot = slug === '';
  return (
    <NavLink
      to={to}
      end={isRoot}
      onClick={onClose}
      className={({ isActive }) =>
        'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ' +
        (isActive ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900') +
        (collapsed ? ' justify-center' : '')
      }
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      {!collapsed && <span className="text-sm font-medium truncate">{label}</span>}
    </NavLink>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────
export function Sidebar({ collapsed, setCollapsed }) {
  const { user, logout, isAdmin, isEmployee } = useAuth();
  const navigate = useNavigate();
  const [mob, setMob] = useState(false);

  const nav  = isAdmin ? ADMIN : isEmployee ? EMP : ACCT;
  const base = '/' + (user?.role || 'admin') + '/';

  const handleLogout = () => { logout(); navigate('/login'); };

  function Content() {
    return (
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className={'flex items-center px-4 py-5 border-b border-gray-100 ' + (collapsed ? 'justify-center' : 'justify-start')}>
          <img
            src="/logo.png"
            alt="VR Industries"
            className={collapsed ? 'w-12 h-12 object-contain' : 'w-40 h-14 object-contain'}
          />
        </div>

        {/* Nav items */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {nav.map((item, i) =>
            item.group
              ? <Group key={i} item={item} base={base} collapsed={collapsed} />
              : <Item key={item.slug} slug={item.slug} label={item.label} Icon={item.Icon} base={base} collapsed={collapsed} onClose={() => setMob(false)} />
          )}
        </nav>

        {/* User footer */}
        <div className="border-t border-gray-100 p-3 space-y-1.5">
          {!collapsed && (
            <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-gray-50">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-indigo-600 font-bold text-sm">
                  {(user?.name || 'U')[0].toUpperCase()}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className={'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors ' + (collapsed ? 'justify-center' : '')}
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="text-sm font-medium">Sign Out</span>}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {mob && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMob(false)} />}
      <button
        onClick={() => setMob(v => !v)}
        className="fixed top-4 left-4 z-40 p-2 bg-white rounded-xl shadow-md border border-gray-100 lg:hidden"
      >
        {mob ? <XMarkIcon className="w-5 h-5" /> : <Bars3Icon className="w-5 h-5" />}
      </button>

      {/* Mobile sidebar */}
      <aside className={'fixed left-0 top-0 h-full w-64 bg-white border-r border-gray-100 z-40 shadow-xl lg:hidden transition-transform duration-300 ' + (mob ? 'translate-x-0' : '-translate-x-full')}>
        <Content />
      </aside>

      {/* Desktop sidebar */}
      <aside className={'hidden lg:flex flex-col fixed left-0 top-0 h-full bg-white border-r border-gray-100 z-30 transition-all duration-300 ' + (collapsed ? 'w-16' : 'w-64')}>
        <Content />
        <button
          onClick={() => setCollapsed(v => !v)}
          className="absolute -right-3 top-20 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50"
        >
          {collapsed
            ? <ChevronDoubleRightIcon className="w-3 h-3 text-gray-500" />
            : <ChevronDoubleLeftIcon  className="w-3 h-3 text-gray-500" />}
        </button>
      </aside>
    </>
  );
}
